import i18n from '../i18n'
import { errorCode } from '../services/api'

/**
 * The public profile's rules (V5-w), pure so the page, the links that open
 * it and the tests share them. The server decides what another learner may
 * see (`social_profile`, the privacy table of api-contract.md §V5-b); this
 * file only shapes that answer — it never reaches for a field the table
 * does not list (no phone, no full name, no birthday, no last-seen time).
 */

/** Where a learner's public profile lives: tapping a name or a photo in the feed, the leaderboard or the team goes here. */
export function profilePath(userId) {
  return `/u/${encodeURIComponent(String(userId))}`
}

/**
 * A `photoUrl` the page may put in an `<img>`: an http(s) or same-origin
 * path, nothing else. Null — draw the shop avatar — for a missing value,
 * which is what every learner gets while `social.photosEnabled` is off.
 */
export function safePhotoUrl(url) {
  if (typeof url !== 'string') return null
  const trimmed = url.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed
  return null
}

/**
 * What a failed read of the profile means: `not_found` (404
 * `user_not_found` — also a deactivated account, so it reads the same as
 * one that never existed), `offline` (never reached the server) or `other`.
 */
export function profileFailure(error) {
  if (!error?.response) return 'offline'
  if (error.response.status === 404 || errorCode(error) === 'user_not_found') return 'not_found'
  return 'other'
}

/**
 * The line under the posts heading when there are none:
 * - `mine` — the learner's own profile, nothing shared yet;
 * - `private` — someone else's while the feed is not public (the server
 *   sends 0 posts then, so no recording is reachable);
 * - `none` — the feed is public and they have shared nothing.
 * Null when there are posts to show.
 */
export function postsNote(profile) {
  if (!profile) return null
  if (Array.isArray(profile.posts) && profile.posts.length > 0) return null
  if (profile.author?.me) return 'mine'
  if (!profile.publicFeed) return 'private'
  return 'none'
}

const BADGE_KEYS = new Set(['weekly_top1', 'monthly_top1', 'team_weekly_top1'])

/** A badge's name in the app's language: the server names badges in Persian only, so English maps the known keys. */
export function badgeLabel(badge, language = i18n.language) {
  if (!badge) return ''
  if (language === 'en' && BADGE_KEYS.has(badge.key)) return i18n.t(`badges.${badge.key}`, { ns: 'publicProfile', lng: 'en' })
  return badge.nameFa || badge.key || ''
}

/** Merges the next page of posts, dropping any the list already has (a post shared between the two reads). */
export function withMorePosts(profile, next) {
  if (!profile) return next
  const seen = new Set(profile.posts.map((post) => post.id))
  return { ...profile, posts: [...profile.posts, ...next.posts.filter((post) => !seen.has(post.id))], nextCursor: next.nextCursor }
}
